/**
 * Neue Richtlinie ohne Datei: Nummer, Titel, Arbeitskreis und Vorlage — das Standardgerüst der
 * GEFMA-Richtlinien oder eine bestehende Richtlinie (nur Gliederung oder mit Inhalt). Der Server
 * legt sie wie einen Import an; danach öffnet sie sich in der Werkstatt.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FilePlus2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useGuidelinesAPI, type GuidelineSummary, type NewGuidelineTemplate, type WorkingGroupRef } from "./GuidelinesAPI";
import { displayTitle } from "./guidelinesPath";

const NONE = "-";

export function NewGuidelineDialog(props: {
  customer: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  guidelines: GuidelineSummary[];
  workingGroups: WorkingGroupRef[];
  onCreated: (documentId: string) => void;
}) {
  const { t } = useTranslation();
  const api = useGuidelinesAPI(props.customer);
  const [number, setNumber] = useState("");
  const [title, setTitle] = useState("");
  const [group, setGroup] = useState(NONE);
  const [source, setSource] = useState<"standard" | "existing">("standard");
  const [from, setFrom] = useState("");
  const [withContent, setWithContent] = useState(false);
  const [busy, setBusy] = useState(false);

  const templates = props.guidelines.filter((g) => !g.superseded);
  const valid = /^\d{3}(-\d{1,2})?$/.test(number.trim()) && title.trim().length > 0 && (source === "standard" || !!from);

  const reset = () => {
    setNumber("");
    setTitle("");
    setGroup(NONE);
    setSource("standard");
    setFrom("");
    setWithContent(false);
  };

  const create = async () => {
    setBusy(true);
    try {
      const template: NewGuidelineTemplate = source === "standard" ? "standard" : withContent ? "copy" : "outline";
      const r = await api.createGuideline({ number: number.trim(), title: title.trim(), workingGroup: group === NONE ? null : group, template, from: source === "standard" ? null : from });
      toast.success(t("guidelinesPage.newGuideline.created", { title: r.title, count: r.sections }));
      props.onOpenChange(false);
      reset();
      props.onCreated(r.documentId);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={props.open}
      onOpenChange={(open) => {
        if (!open) reset();
        props.onOpenChange(open);
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("guidelinesPage.newGuideline.title")}</DialogTitle>
          <DialogDescription>{t("guidelinesPage.newGuideline.intro")}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 text-sm">
          <div className="flex gap-3">
            <label className="flex w-28 flex-col gap-1">
              <span className="text-xs text-muted-foreground">{t("guidelinesPage.newGuideline.number")}</span>
              <Input value={number} onChange={(e) => setNumber(e.target.value)} placeholder="200" inputMode="numeric" data-testid="new-number" />
            </label>
            <label className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="text-xs text-muted-foreground">{t("guidelinesPage.newGuideline.name")}</span>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="new-title" />
            </label>
          </div>
          {props.workingGroups.length ? (
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">{t("guidelinesPage.newGuideline.workingGroup")}</span>
              <Select value={group} onValueChange={setGroup}>
                <SelectTrigger data-testid="new-group">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t("guidelinesPage.newGuideline.noGroup")}</SelectItem>
                  {props.workingGroups.map((g) => (
                    <SelectItem key={g.key} value={g.key}>
                      {g.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <div className="flex flex-col gap-2">
            <span className="text-xs text-muted-foreground">{t("guidelinesPage.newGuideline.template")}</span>
            <RadioGroup value={source} onValueChange={(v) => setSource(v as "standard" | "existing")}>
              <div className="flex items-start gap-2">
                <RadioGroupItem value="standard" id="new-standard" className="mt-0.5" />
                <Label htmlFor="new-standard" className="flex flex-col gap-0.5 font-normal">
                  <span>{t("guidelinesPage.newGuideline.standard")}</span>
                  <span className="text-xs text-muted-foreground">{t("guidelinesPage.newGuideline.standardHint")}</span>
                </Label>
              </div>
              <div className="flex items-start gap-2">
                <RadioGroupItem value="existing" id="new-existing" className="mt-0.5" disabled={!templates.length} />
                <Label htmlFor="new-existing" className="font-normal">
                  {t("guidelinesPage.newGuideline.existing")}
                </Label>
              </div>
            </RadioGroup>
            {source === "existing" ? (
              <div className="ml-6 flex flex-col gap-2">
                <Select value={from} onValueChange={setFrom}>
                  <SelectTrigger data-testid="new-from">
                    <SelectValue placeholder={t("guidelinesPage.newGuideline.pick")} />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((g) => (
                      <SelectItem key={g._id} value={g._id}>
                        {displayTitle(g.title)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <RadioGroup value={withContent ? "copy" : "outline"} onValueChange={(v) => setWithContent(v === "copy")}>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="outline" id="new-outline" />
                    <Label htmlFor="new-outline" className="font-normal">
                      {t("guidelinesPage.newGuideline.outline")}
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="copy" id="new-copy" />
                    <Label htmlFor="new-copy" className="font-normal">
                      {t("guidelinesPage.newGuideline.copy")}
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            ) : null}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => props.onOpenChange(false)}>
            {t("guidelinesPage.cancel")}
          </Button>
          <Button onClick={create} disabled={!valid || busy} data-testid="new-create">
            <FilePlus2 size={14} className={busy ? "mr-1 animate-pulse" : "mr-1"} />
            {busy ? t("guidelinesPage.newGuideline.busy") : t("guidelinesPage.newGuideline.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
